import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { founders } from '../mock';
import { Card } from '../components/ui/card';
import { CheckCircle2 } from 'lucide-react';

const About = () => {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#021F3F] via-[#0A3D62] to-[#021F3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t.about.title}
          </h1>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto" />
        </div>
      </section>

      {/* Company Description */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-gray-700 leading-relaxed text-center mb-8">
            {t.about.description}
          </p>
          <p className="text-base text-gray-600 leading-relaxed text-center">
            {t.about.descriptionExtended}
          </p>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-20 bg-[#F8F8F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#021F3F] mb-6">
              {t.about.approachTitle}
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-8" />
            <p className="text-lg text-gray-600 leading-relaxed">
              {t.about.approachText}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#021F3F] mb-4">
              {t.about.valuesTitle}
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6" />
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t.about.valuesIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.about.valuesExtended.map((item, index) => (
              <Card key={index} className="p-6 border-none shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#E6C76D] flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="text-[#021F3F]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#021F3F] mb-2">{item.value}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-[#F8F8F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Vision */}
            <Card className="p-8 border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-[#0A3D62] text-white">
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full bg-[#D4AF37] flex items-center justify-center mb-4">
                  <span className="text-3xl">🎯</span>
                </div>
                <h3 className="text-3xl font-bold mb-4">{t.about.visionTitle}</h3>
                <p className="text-xl text-gray-200 leading-relaxed mb-4">{t.about.vision}</p>
              </div>
              <p className="text-base text-gray-300 leading-relaxed">{t.about.visionExtended}</p>
            </Card>

            {/* Mission */}
            <Card className="p-8 border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#E6C76D] flex items-center justify-center mb-4">
                  <span className="text-3xl">🚀</span>
                </div>
                <h3 className="text-3xl font-bold text-[#021F3F] mb-4">{t.about.missionTitle}</h3>
                <p className="text-xl text-gray-700 leading-relaxed mb-4">{t.about.mission}</p>
              </div>
              <p className="text-base text-gray-600 leading-relaxed">{t.about.missionExtended}</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#021F3F] mb-4">
              {t.about.foundersTitle}
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6" />
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t.about.foundersIntro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {founders.map((founder, index) => (
              <Card
                key={index}
                className="p-8 border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex flex-col items-center text-center">
                  {/* Founder Photo */}
                  <div className="relative mb-6">
                    <div className="w-40 h-40 rounded-full overflow-hidden shadow-xl ring-4 ring-[#D4AF37] ring-offset-4">
                      <img
                        src={founder.photo}
                        alt={founder.name}
                        className="w-full h-full object-cover"
                        style={{ transform: index === 0 ? 'scale(1.0)' : 'scale(1.1)' }}
                      />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-[#021F3F] mb-4">
                    {founder.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {founder.description[language]}
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Shared Vision */}
          <div className="mt-16 max-w-3xl mx-auto">
            <Card className="p-8 bg-gradient-to-br from-[#021F3F] to-[#0A3D62] border-none shadow-xl">
              <p className="text-xl text-gray-200 text-center leading-relaxed">
                {t.about.sharedVision}
              </p>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;